import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, ToggleLeft, ToggleRight, Split, Minus, Sun, Moon, Save, Loader2, Edit2, X } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import PrimaryButton from '../ui/PrimaryButton';
import TimePicker from '../ui/TimePicker';
import ConfirmDialog from '../ui/ConfirmDialog';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useEvents } from '@/contexts/EventsContext';
import { useClients } from '@/contexts/ClientsContext';
import { DEFAULT_OPEN_TIME, DEFAULT_CLOSE_TIME, DEFAULT_BREAK_START, DEFAULT_BREAK_END, DAYS_ORDER } from '@/lib/constants';

interface StaffHoursTabProps {
    data: any;
    onSave: (data: any) => Promise<any>;
    readOnly?: boolean;
}

type DaySchedule = {
    isOpen: boolean;
    isSplit: boolean;
    start1: string;
    end1: string;
    start2: string;
    end2: string;
};

const StaffHoursTab: React.FC<StaffHoursTabProps> = ({ data, onSave, readOnly = false }) => {
    const { t } = useTranslation();
    const [isSaving, setIsSaving] = useState(false);
    const { bookings, shiftChanges, getPerformanceById } = useEvents();
    const { getClientById } = useClients();
    // Salvataggio in attesa di conferma: nuovi orari + prenotazioni future che ne cadrebbero fuori
    const [pendingSave, setPendingSave] = useState<{ ranges: any; conflicts: any[] } | null>(null);
    const parseSchedule = (staffData: any) => {
        if (!staffData?.time_ranges) return {};
        const newSchedule: any = {};
        DAYS_ORDER.forEach(key => {
            const time = staffData.time_ranges[key] || {};
            const isOpen = !!time.open;
            const isSplit = !!(time.start_break && time.end_break);

            newSchedule[key] = {
                isOpen,
                isSplit,
                start1: time.open || DEFAULT_OPEN_TIME,
                end1: isSplit ? (time.start_break || DEFAULT_BREAK_START) : (time.close || DEFAULT_CLOSE_TIME),
                start2: time.end_break || DEFAULT_BREAK_END,
                end2: time.close || DEFAULT_CLOSE_TIME
            };
        });
        return newSchedule;
    };

    const [schedule, setSchedule] = useState<Record<string, DaySchedule>>(() => parseSchedule(data));

    const [isEditing, setIsEditing] = useState(false);

    // Update schedule if data changes externally
    useEffect(() => {
        setSchedule(parseSchedule(data));
    }, [data]);

    const handleCancel = () => {
        setSchedule(parseSchedule(data));
        setIsEditing(false);
    };

    const toggleDayOpen = (day: string) => {
        setSchedule(prev => ({ ...prev, [day]: { ...prev[day], isOpen: !prev[day].isOpen } }));
    };

    const toggleSplitShift = (day: string) => {
        setSchedule(prev => ({
            ...prev,
            [day]: {
                ...prev[day],
                isSplit: !prev[day].isSplit,
                end1: !prev[day].isSplit ? DEFAULT_BREAK_START : DEFAULT_CLOSE_TIME
            }
        }));
    };

    const updateTime = (day: string, field: keyof DaySchedule, value: string) => {
        setSchedule(prev => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        const newTimeRanges: any = {};
        let isValid = true;

        DAYS_ORDER.forEach(key => {
            if (!isValid) return;

            const cfg = schedule[key];
            if (!cfg.isOpen) {
                newTimeRanges[key] = { open: null, close: null, start_break: null, end_break: null };
            } else {
                if (cfg.isSplit) {
                    if (!(cfg.start1 < cfg.end1)) {
                        toast.error(t('staff.hours.error_morning', { day: t(`common.days.${key}`) }));
                        isValid = false;
                    } else if (!(cfg.end1 < cfg.start2)) {
                        toast.error(t('staff.hours.error_break', { day: t(`common.days.${key}`) }));
                        isValid = false;
                    } else if (!(cfg.start2 < cfg.end2)) {
                        toast.error(t('staff.hours.error_afternoon', { day: t(`common.days.${key}`) }));
                        isValid = false;
                    }

                    if (isValid) {
                        newTimeRanges[key] = { open: cfg.start1, start_break: cfg.end1, end_break: cfg.start2, close: cfg.end2 };
                    }
                } else {
                    if (!(cfg.start1 < cfg.end1)) {
                        toast.error(t('staff.hours.error_continuous', { day: t(`common.days.${key}`) }));
                        isValid = false;
                    }

                    if (isValid) {
                        newTimeRanges[key] = { open: cfg.start1, close: cfg.end1, start_break: null, end_break: null };
                    }
                }
            }
        });

        if (!isValid) {
            setIsSaving(false);
            return;
        }

        // Prenotazioni future confermate che cadrebbero fuori dai nuovi orari
        // (giorno di riposo, fuori fascia o dentro la pausa). Le date con un
        // ShiftChange non sono toccate: lì vale l'orario dell'eccezione.
        const conflicts = findConflicts(newTimeRanges);
        if (conflicts.length > 0) {
            setIsSaving(false);
            setPendingSave({ ranges: newTimeRanges, conflicts });
            return;
        }

        await doSave(newTimeRanges);
    };

    const findConflicts = (newTimeRanges: any) => {
        const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const now = new Date();
        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        const toMin = (v: string) => {
            const [h, m] = v.slice(0, 5).split(':').map(Number);
            return h * 60 + (m || 0);
        };

        return (bookings || [])
            .filter((b: any) => {
                if (b.hairdresser_id !== data?.id || b.status !== 'confirmed') return false;
                if (!b.date || !b.time || b.date < todayStr) return false;
                if ((shiftChanges || []).some((s: any) => s.hairdresser_id === data.id && s.shift_date === b.date)) return false;

                const dayKey = weekdays[new Date(`${b.date}T00:00:00`).getDay()];
                const rng = newTimeRanges[dayKey];
                if (!rng?.open || !rng?.close) return true;

                const start = toMin(b.time);
                const end = start + (getPerformanceById(b.performance_id)?.duration || 30);
                if (start < toMin(rng.open) || end > toMin(rng.close)) return true;
                if (rng.start_break && rng.end_break && start < toMin(rng.end_break) && end > toMin(rng.start_break)) return true;
                return false;
            })
            .sort((a: any, b: any) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`));
    };

    const doSave = async (newTimeRanges: any) => {
        setIsSaving(true);
        try {
            const res = await onSave({ ...data, time_ranges: newTimeRanges });
            if (res.success) {
                toast.success(t('staff.hours.toast.saved'));
                setIsEditing(false);
            } else {
                toast.error(t('common.save_error'));
            }
        } catch (e) {
            toast.error(t('common.unexpected_error'));
        } finally {
            setIsSaving(false);
        }
    };

    const conflictsSummary = (conflicts: any[]) => {
        const items = conflicts.slice(0, 5).map((b: any) => {
            const name = getClientById?.(b.client_id)?.full_name || t('bookings.modal.client_not_found', 'Cliente');
            return `${format(new Date(`${b.date}T00:00:00`), 'EEE d MMM', { locale: it })} ${b.time.slice(0, 5)} — ${name}`;
        });
        const extra = conflicts.length > 5
            ? ` ${t('staff.hours.conflicts_more', 'e altri {{count}}.', { count: conflicts.length - 5 })}`
            : '';
        return `${items.join(' · ')}${extra}`;
    };

    if (Object.keys(schedule).length === 0) return null;

    const tabContentVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
        exit: { opacity: 0, y: -10, transition: { duration: 0.2 } }
    };

    return (
        <motion.div
            key="hours"
            variants={tabContentVariants}
            initial="hidden" animate="visible" exit="exit"
            className="space-y-4 pt-4 relative px-4 md:px-6"
        >
            {!readOnly && (
                <div className="flex justify-between items-center bg-gray-50 p-4 rounded-xl border border-gray-100 mb-2">
                    <div>
                        <h3 className="text-sm font-bold text-gray-900">{t('staff.hours.config_title')}</h3>
                        <p className="text-xs text-gray-500">{t('staff.hours.config_desc')}</p>
                    </div>

                    <button
                        onClick={isEditing ? handleCancel : () => setIsEditing(true)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${isEditing ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-white border border-gray-200 text-gray-700 shadow-sm'}`}
                    >
                        {isEditing ? <><X size={14} /> {t('common.cancel')}</> : <><Edit2 size={14} /> {t('common.edit')}</>}
                    </button>
                </div>
            )}

            <div className="space-y-3 pb-8">
                {DAYS_ORDER.map((day) => {
                    const dayConfig = schedule[day];
                    // Defensive check in case schedule is not yet initialized for this key
                    if (!dayConfig) return null;

                    const isOpen = dayConfig.isOpen;
                    const isSplit = dayConfig.isSplit;

                    // Svincolato dagli orari del negozio
                    const minTime = DEFAULT_OPEN_TIME;
                    const maxTime = DEFAULT_CLOSE_TIME;

                    return (
                        <div key={day} className={`bg-white border rounded-2xl p-4 transition-all ${isOpen ? 'border-gray-200 shadow-sm' : 'border-gray-100 opacity-60 bg-gray-50/50'}`}>

                            {/* HEADER GIORNO */}
                            <div className="flex justify-between items-center mb-2">
                                <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold capitalize ${isOpen ? 'bg-clip-50 text-clip-600' : 'bg-gray-200 text-gray-500'}`}>
                                        {t(`common.days.${day}`).substring(0, 3)}
                                    </div>
                                    <span className={`font-bold capitalize ${isOpen ? 'text-gray-900' : 'text-gray-400'}`}>{t(`common.days.${day}`)}</span>
                                </div>

                                {isEditing ? (
                                    <button
                                        onClick={() => toggleDayOpen(day)}
                                        className={`flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${isOpen ? 'bg-green-50 text-green-700' : 'bg-gray-200 text-gray-500'}`}
                                    >
                                        {isOpen ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                                        <span className="hidden sm:inline">
                                            {isOpen ? t('staff.hours.working') : t('staff.hours.rest')}
                                        </span>
                                    </button>
                                ) : (
                                    <span className={`text-xs font-bold px-2 py-1 rounded ${isOpen ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                                        {isOpen ? t('staff.hours.working') : t('staff.hours.rest')}
                                    </span>
                                )}
                            </div>

                            {/* CONTENUTO ORARI */}
                            {isOpen && (
                                <div className="mt-4 pt-4 border-t border-gray-50">
                                    {isEditing ? (
                                        <div className="space-y-4">
                                            {/* TOGGLE SPLIT / CONTINUOUS */}
                                            <div className="flex justify-center bg-gray-50 p-1 rounded-lg">
                                                <button
                                                    onClick={() => !isSplit && toggleSplitShift(day)}
                                                    className={`flex-1 flex items-center justify-center gap-2 py-1.5 rounded-md text-xs font-bold transition-all ${isSplit ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                                                >
                                                    <Split size={14} /> {t('staff.hours.split')}
                                                </button>
                                                <button
                                                    onClick={() => isSplit && toggleSplitShift(day)}
                                                    className={`flex-1 flex items-center justify-center gap-2 py-1.5 rounded-md text-xs font-bold transition-all ${!isSplit ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                                                >
                                                    <Minus size={14} /> {t('staff.hours.continuous')}
                                                </button>
                                            </div>

                                            {/* INPUT GRID */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {isSplit ? (
                                                    <>
                                                        {/* MATTINA */}
                                                        <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 flex flex-col gap-2">
                                                            <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                                                <Sun size={14} className="text-orange-400" />
                                                                {t('staff.hours.morning')}
                                                            </div>
                                                            <div className="flex items-center gap-2 w-full">
                                                                {/* min-w-0 è fondamentale per prevenire l'overflow su mobile */}
                                                                <div className="flex-1 min-w-0">
                                                                    <TimePicker value={dayConfig.start1} onChange={(val) => updateTime(day, 'start1', val)} minTime={minTime} maxTime={maxTime} step={5} />
                                                                </div>
                                                                <span className="text-gray-300 font-bold shrink-0">-</span>
                                                                <div className="flex-1 min-w-0">
                                                                    <TimePicker value={dayConfig.end1} onChange={(val) => updateTime(day, 'end1', val)} minTime={minTime} maxTime={maxTime} step={5} />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* POMERIGGIO */}
                                                        <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 flex flex-col gap-2">
                                                            <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                                                <Moon size={14} className="text-indigo-400" />
                                                                {t('staff.hours.afternoon')}
                                                            </div>
                                                            <div className="flex items-center gap-2 w-full">
                                                                <div className="flex-1 min-w-0">
                                                                    <TimePicker value={dayConfig.start2} onChange={(val) => updateTime(day, 'start2', val)} minTime={minTime} maxTime={maxTime} step={5} />
                                                                </div>
                                                                <span className="text-gray-300 font-bold shrink-0">-</span>
                                                                <div className="flex-1 min-w-0">
                                                                    <TimePicker value={dayConfig.end2} onChange={(val) => updateTime(day, 'end2', val)} minTime={minTime} maxTime={maxTime} step={5} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    /* CONTINUATO */
                                                    <div className="col-span-1 md:col-span-2 bg-gray-50 p-3 rounded-xl border border-gray-100 flex flex-col gap-2">
                                                        <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                                            <Clock size={14} className="text-clip-500" />
                                                            {t('staff.hours.schedule')}
                                                        </div>
                                                        <div className="flex items-center gap-2 w-full">
                                                            <div className="flex-1 min-w-0">
                                                                <TimePicker value={dayConfig.start1} onChange={(val) => updateTime(day, 'start1', val)} minTime={minTime} maxTime={maxTime} step={5} />
                                                            </div>
                                                            <span className="text-gray-300 font-bold shrink-0">-</span>
                                                            <div className="flex-1 min-w-0">
                                                                <TimePicker value={dayConfig.end1} onChange={(val) => updateTime(day, 'end1', val)} minTime={minTime} maxTime={maxTime} step={5} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-sm text-gray-600 font-medium pl-0 md:pl-11">
                                            {isSplit ? (
                                                <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                                                    <span>{dayConfig.start1} - {dayConfig.end1}</span>
                                                    <span className="text-gray-300 hidden sm:inline">/</span>
                                                    <span>{dayConfig.start2} - {dayConfig.end2}</span>
                                                </div>
                                            ) : (
                                                <span>{dayConfig.start1} - {dayConfig.end1} ({t('staff.hours.continuous')})</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            {isEditing && (
                <div className="sticky bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur-md border-t border-gray-100 flex justify-end z-20 -mx-4 md:rounded-b-3xl">
                    <PrimaryButton label={isSaving ? t('common.saving') : t('staff.hours.save')} icon={isSaving ? Loader2 : Save} className="w-full md:w-auto justify-center shadow-lg" onClick={handleSave} disabled={isSaving} />
                </div>
            )}

            {/* Conferma salvataggio con prenotazioni fuori dai nuovi orari */}
            <ConfirmDialog
                isOpen={!!pendingSave}
                title={t('staff.hours.conflicts_title', 'Prenotazioni fuori dai nuovi orari')}
                description={pendingSave ? t('staff.hours.conflicts_desc', '{{count}} appuntamenti futuri cadono fuori dai nuovi orari: {{list}} Salvando restano confermati: spostali o avvisa i clienti interessati.', { count: pendingSave.conflicts.length, list: conflictsSummary(pendingSave.conflicts) + '.' }) : ''}
                confirmLabel={t('staff.hours.conflicts_confirm', 'Salva comunque')}
                cancelLabel={t('common.cancel', 'Annulla')}
                variant="danger"
                loading={isSaving}
                onConfirm={async () => {
                    if (!pendingSave) return;
                    await doSave(pendingSave.ranges);
                    setPendingSave(null);
                }}
                onCancel={() => !isSaving && setPendingSave(null)}
            />
        </motion.div>
    );
};

export default StaffHoursTab;